/** 部署进度状态推导纯函数（与 DeployProgress 组件分离，满足 fast-refresh 只导出组件约定）。 */

import type { SseEvent } from '../api/sse';

/** B13 部署后确定性验证结论（后端 verification_result 的展示子集）。 */
export interface DeploymentVerification {
  healthy?: boolean;
  method?: string;
  status_code?: number | null;
  latency_ms?: number;
  error?: string | null;
  suggestion?: string | null;
  details?: Record<string, unknown> | null;
  checked_at?: string;
}

/** 部署进度视图状态（由事件流纯函数推导，刷新页面后可从事件重建）。 */
export interface DeployProgressState {
  stage: string | null;
  status: string;
  error: string | null;
  /** 部署后确定性验证结论（completed stage / done result 事件携带，B13）。 */
  verification: DeploymentVerification | null;
  /** 验证结论一行摘要（中文，后端 summarize_verification 输出）。 */
  verificationSummary: string | null;
}

export const INITIAL_STATE: DeployProgressState = {
  stage: 'pending',
  status: 'pending',
  error: null,
  verification: null,
  verificationSummary: null,
};

/** 从事件 data 中提取验证结论（若该 stage 事件携带）。 */
function extractVerification(data: Record<string, unknown> | null | undefined): {
  verification: DeploymentVerification | null;
  verificationSummary: string | null;
} {
  const payload = data ?? {};
  const raw = payload.verification;
  const verification = raw && typeof raw === 'object' ? (raw as DeploymentVerification) : null;
  const summary = typeof payload.verification_summary === 'string' ? payload.verification_summary : null;
  return { verification, verificationSummary: summary };
}

/** 从任务事件推导进度视图（stage 迁移 + done 终态 + 验证结论）。 */
export function applyDeployEvent(state: DeployProgressState, event: SseEvent): DeployProgressState {
  const data = (event.data || {}) as Record<string, unknown>;
  switch (event.type) {
    case 'stage': {
      const { verification, verificationSummary } = extractVerification(data);
      return {
        ...state,
        stage: String(data.stage || state.stage || 'pending'),
        // 只有携带验证结论的 stage 事件才更新验证行（verifying / completed）
        ...(verification
          ? { verification, verificationSummary }
          : { verificationSummary: verificationSummary ?? state.verificationSummary }),
      };
    }
    case 'done':
      if (data.status === 'succeeded') {
        const { verification } = extractVerification(data?.result as Record<string, unknown>);
        return {
          ...state,
          stage: 'completed',
          status: 'success',
          error: null,
          // 只更新验证结论本体；done 事件不一定携带 summary，保留已有摘要不覆盖
          ...(verification ? { verification } : {}),
        };
      }
      return {
        ...state,
        stage: state.stage === 'completed' ? state.stage : 'failed',
        status: 'failed',
        error: typeof data.error === 'string' && data.error ? data.error : '部署失败',
      };
    default:
      return state;
  }
}
